import React, { useState } from 'react';
import { FileText, Check, X, AlertTriangle, Download, Upload, List } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для массовой проверки URL
 */
const BulkChecker = ({ onBulkCheck, bulkResults, isChecking }) => {
  const [urls, setUrls] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Обработка ввода URL
  const handleUrlInput = (e) => {
    setUrls(e.target.value);
    setValidationError('');
  };

  // Подготовка и валидация URL перед массовой проверкой
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!urls.trim()) {
      setValidationError('Пожалуйста, введите хотя бы один URL для проверки');
      return;
    }
    
    // Разбиваем текст на отдельные URL (по переносу строки или запятой)
    const urlList = urls.split(/[\n,]+/)
      .map(url => url.trim())
      .filter(url => url); // Фильтруем пустые строки
      
    if (urlList.length === 0) {
      setValidationError('Не удалось распознать URL. Введите каждый URL в новой строке или через запятую');
      return;
    }
    
    if (urlList.length > 50) {
      setValidationError('Максимальное количество URL для одновременной проверки - 50');
      return;
    }
    
    // Простая валидация URL
    const invalidUrls = urlList.filter(url => {
      // Считаем валидными URL, начинающиеся с http:// или https://, а также простые доменные имена
      const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
      return !urlPattern.test(url);
    });
    
    if (invalidUrls.length > 0) {
      setValidationError(`Обнаружены некорректные URL: ${invalidUrls.join(', ')}`);
      return;
    }
    
    // Запускаем массовую проверку
    onBulkCheck(urlList);
  };

  // Экспорт результатов в CSV
  const exportToCsv = () => {
    if (!bulkResults || bulkResults.length === 0) return;
    
    // Подготовка данных для CSV
    const headers = [
      'URL', 
      'Код статуса', 
      'Статус', 
      'Итоговый URL', 
      'Количество редиректов', 
      'Общее время (мс)',
      'Защищенное соединение',
      'SEO-оценка'
    ];
    
    const rows = bulkResults.map(item => {
      if (item.error) {
        return [
          item.url, 
          'Ошибка', 
          item.error, 
          '', 
          '', 
          '', 
          '',
          ''
        ];
      }
      
      const { result } = item;
      const { summary, chain } = result;
      const finalStatus = chain[chain.length - 1].status;
      const statusText = chain[chain.length - 1].statusText;
      
      return [
        item.url,
        finalStatus,
        statusText,
        summary.finalUrl,
        summary.redirectCount,
        summary.totalTime,
        summary.isSecureConnection ? 'Да' : 'Нет',
        summary.seoImpact.label
      ];
    });
    
    // Создание контента CSV
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    // Создание и скачивание файла
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'результаты_проверки_url.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Индикатор статуса для результатов
  const getStatusIndicator = (status) => {
    if (!status || status === 'Ошибка') {
      return <X className="w-5 h-5 text-red-500" />;
    } else if (status >= 200 && status < 300) {
      return <Check className="w-5 h-5 text-green-500" />;
    } else if (status >= 300 && status < 400) {
      return <FileText className="w-5 h-5 text-blue-500" />;
    } else if (status >= 400) {
      return <AlertTriangle className="w-5 h-5 text-orange-500" />;
    }
    return null;
  };

  // Загрузка списка URL из файла
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Читаем только текстовые файлы
    if (file.type !== 'text/plain' && file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      setValidationError('Пожалуйста, загрузите текстовый файл (.txt или .csv)');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setUrls(e.target.result);
      setValidationError('');
    };
    reader.onerror = () => {
      setValidationError('Ошибка при чтении файла');
    };
    reader.readAsText(file);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center w-full justify-between text-lg font-medium text-gray-900 dark:text-white mb-2"
      >
        <div className="flex items-center">
          <List className="w-5 h-5 mr-2 text-indigo-500" />
          Массовая проверка URL
        </div>
        <div className="text-indigo-500">
          {isOpen ? '▲' : '▼'}
        </div>
      </button>
      
      {isOpen && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Проверьте сразу несколько URL на наличие редиректов. Введите каждый URL в новой строке или загрузите текстовый файл со списком.
          </p>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <textarea
                className="form-textarea w-full h-32"
                placeholder="Введите URL для проверки, каждый с новой строки. Например:&#10;example.com&#10;https://другой-сайт.рф&#10;www.site.ru/page"
                value={urls}
                onChange={handleUrlInput}
                disabled={isChecking}
              ></textarea>
              {validationError && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {validationError}
                </p>
              )}
            </div>
            
            <div className="flex flex-wrap gap-3 mb-4">
              <Button
                type="submit"
                primary
                icon={<FileText className="w-4 h-4" />}
                disabled={isChecking || !urls.trim()}
                isLoading={isChecking}
              >
                {isChecking ? 'Проверяем...' : 'Проверить URL'}
              </Button>
              
              <div className="relative">
                <Button
                  type="button"
                  variant="outline"
                  icon={<Upload className="w-4 h-4" />}
                  disabled={isChecking}
                >
                  Загрузить файл
                </Button>
                <input
                  type="file"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept=".txt,.csv"
                  onChange={handleFileUpload}
                  disabled={isChecking}
                />
              </div>
              
              {bulkResults && bulkResults.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  icon={<Download className="w-4 h-4" />}
                  onClick={exportToCsv}
                >
                  Экспорт в CSV
                </Button>
              )}
            </div>
          </form>
          
          {/* Результаты массовой проверки */}
          {bulkResults && bulkResults.length > 0 && (
            <div className="mt-4">
              <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">
                Результаты ({bulkResults.length} URL)
              </h4>
              
              <div className="overflow-x-auto max-h-96 custom-scrollbar border border-gray-200 dark:border-gray-700 rounded-md">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">URL</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Статус</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Редиректы</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Итоговый URL</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {bulkResults.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {item.url}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {item.error ? (
                            <div className="flex items-center text-red-500">
                              <X className="w-4 h-4 mr-1" /> Ошибка
                            </div>
                          ) : (
                            <div className="flex items-center">
                              {getStatusIndicator(item.result?.chain[item.result.chain.length - 1].status)}
                              <span className="ml-1">
                                {item.result?.chain[item.result.chain.length - 1].status} 
                                {item.result?.chain[item.result.chain.length - 1].statusText}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {item.error ? '-' : item.result.summary.redirectCount}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {item.error ? item.error : item.result.summary.finalUrl}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                {bulkResults.filter(item => item.error).length > 0 && (
                  <p className="text-orange-500 dark:text-orange-400 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Найдено ошибок: {bulkResults.filter(item => item.error).length}
                  </p>
                )}
                {bulkResults.filter(item => !item.error && item.result.summary.redirectCount > 0).length > 0 && (
                  <p className="text-blue-500 dark:text-blue-400">
                    URL с редиректами: {bulkResults.filter(item => !item.error && item.result.summary.redirectCount > 0).length}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkChecker;